/**
 * Metadados VISUAIS dos nós do Comercial nas automações — negócios, funis,
 * contratos, projetos e clientes (issue #156).
 *
 * Os campos e rótulos vêm do catálogo do backend
 * (api/automations/comercial_negocios_nodes.py); aqui ficam só o ícone/tom e o
 * resumo de uma linha do card. Separado de nodeMeta.js (que importa este
 * arquivo) para não misturar com os demais módulos.
 */

export const COMERCIAL_NEGOCIOS_NODE_META = {
  'trigger.deal_updated': { icon: 'edit' },
  'trigger.deal_value_changed': { icon: 'dollar_circle' },
  'trigger.deal_owner_changed': { icon: 'user' },
  'trigger.deal_reopened': { icon: 'refresh' },
  'trigger.deal_archived': { icon: 'inbox' },
  'trigger.deal_deleted': { icon: 'trash' },
  'trigger.deal_won': { icon: 'crown' },
  'trigger.deal_lost': { icon: 'close' },
  'trigger.deal_contact_added': { icon: 'users' },
  'trigger.deal_contact_removed': { icon: 'users' },
  'trigger.deal_item_added': { icon: 'plus' },
  'trigger.deal_item_removed': { icon: 'trash' },
  'trigger.deal_no_activity': { icon: 'timer' },
  'trigger.pipeline_created': { icon: 'columns' },
  'trigger.stage_created': { icon: 'columns' },
  'trigger.stage_changed': { icon: 'edit' },
  'trigger.contract_created': { icon: 'page' },
  'trigger.contract_status_changed': { icon: 'refresh' },
  'trigger.contract_expiring': { icon: 'calendar' },
  'trigger.project_created': { icon: 'folder' },
  'trigger.project_status_changed': { icon: 'refresh' },
  'trigger.project_deadline_near': { icon: 'clock' },
  'trigger.project_overdue': { icon: 'alert' },
  'trigger.client_created': { icon: 'star' },
  'action.update_deal_owner': { icon: 'user' },
  'action.add_deal_item': { icon: 'plus' },
  'action.add_deal_contact': { icon: 'users' },
  'action.archive_deal': { icon: 'inbox', tone: 'slate' },
  'action.reopen_deal': { icon: 'refresh' },
  'action.duplicate_deal': { icon: 'dollar_circle' },
  'action.set_loss_reason': { icon: 'close' },
  'action.set_origin': { icon: 'target' },
  'action.change_pipeline': { icon: 'columns' },
  'action.create_contract': { icon: 'page' },
  'action.update_contract_status': { icon: 'refresh' },
  'action.create_project': { icon: 'folder' },
  'action.update_project_status': { icon: 'refresh' },
  'action.convert_to_client': { icon: 'star' },
}

const trunc = (s, n = 40) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '')

const CONTRATO = { ativo: 'ativo', inadimplente: 'inadimplente', cancelado: 'cancelado', encerrado: 'encerrado' }
const PROJETO = { ativo: 'ativo', pausado: 'pausado', concluido: 'concluído', arquivado: 'arquivado' }
const PAPEL = {
  decisor: 'decisor', influenciador: 'influenciador', contato: 'contato',
  tecnico: 'técnico', financeiro: 'financeiro', outro: 'outro',
}
const CAMPO_NEGOCIO = {
  titulo: 'título', descricao: 'descrição', empresa: 'empresa', privado: 'privado',
  campanha: 'campanha', justificativa: 'justificativa',
}
const CAMPO_ETAPA = { nome: 'nome', probabilidade: 'probabilidade', tipo: 'tipo', excluida: 'exclusão' }
const TIPO_FUNIL = {
  comercial: 'comercial', pre_venda: 'pré-venda', pos_venda: 'pós-venda', operacao: 'operação',
}
const PERIODO = { mensal: 'mensal', trimestral: 'trimestral', semestral: 'semestral', anual: 'anual', unico: 'único' }
const COPIA = { sim: 'sim', nao: 'não' }

/** Sufixo de filtro de funil: " · em Vendas". */
function emFunil(config, nome) {
  return config.pipeline_id ? ` · ${nome(config.pipeline_id) || 'funil selecionado'}` : ''
}

/**
 * Resumo de uma linha dos nós do Comercial. Devolve `undefined` para tipos que
 * não são destes módulos (quem chama segue para o resumo dos demais nós).
 */
export function summarizeComercialNegocios(key, config = {}, names = {}) {
  const nome = (id) => names[id] || null
  switch (key) {
    case 'trigger.deal_updated':
      return `${config.field ? `Mudou ${CAMPO_NEGOCIO[config.field] || config.field}` : 'Negócio atualizado'}${emFunil(config, nome)}`
    case 'trigger.deal_value_changed':
      return `${config.direction === 'up' ? 'Valor aumentou' : config.direction === 'down' ? 'Valor diminuiu' : 'Valor alterado'}${emFunil(config, nome)}`
    case 'trigger.deal_owner_changed':
      return config.user_id ? `Passou para ${nome(config.user_id) || 'pessoa selecionada'}${emFunil(config, nome)}` : `Qualquer mudança de responsável${emFunil(config, nome)}`
    case 'trigger.deal_reopened':
      return `Negócio reaberto${emFunil(config, nome)}`
    case 'trigger.deal_archived':
      return `Negócio arquivado${emFunil(config, nome)}`
    case 'trigger.deal_deleted':
      return `Negócio excluído${emFunil(config, nome)}`
    case 'trigger.deal_won':
      return `Negócio ganho${emFunil(config, nome)}`
    case 'trigger.deal_lost':
      return config.loss_reason ? `Perdido por “${trunc(config.loss_reason, 24)}”${emFunil(config, nome)}` : `Negócio perdido${emFunil(config, nome)}`
    case 'trigger.deal_contact_added':
      return config.papel ? `Lead adicionado (${PAPEL[config.papel] || config.papel})${emFunil(config, nome)}` : `Lead adicionado ao negócio${emFunil(config, nome)}`
    case 'trigger.deal_contact_removed':
      return config.papel ? `Lead removido (${PAPEL[config.papel] || config.papel})${emFunil(config, nome)}` : `Lead removido do negócio${emFunil(config, nome)}`
    case 'trigger.deal_item_added':
    case 'trigger.deal_item_removed': {
      const verbo = key.endsWith('added') ? 'adicionado' : 'removido'
      const quem = config.name ? `“${trunc(config.name, 24)}”` : config.tipo === 'servico' ? 'serviço' : config.tipo === 'produto' ? 'produto' : 'item'
      return `${quem[0].toUpperCase()}${quem.slice(1)} ${verbo}${emFunil(config, nome)}`
    }
    case 'trigger.deal_no_activity': {
      if (!config.days) return 'Informe os dias sem atividade'
      const onde = config.stage_id ? ` em ${nome(config.stage_id) || 'etapa selecionada'}` : emFunil(config, nome).replace(' · ', ' em ')
      return `Sem atividade há ${config.days} dia(s)${onde}`
    }
    case 'trigger.pipeline_created':
      return config.tipo ? `Funil ${TIPO_FUNIL[config.tipo] || config.tipo} criado` : 'Qualquer funil criado'
    case 'trigger.stage_created':
      return config.pipeline_id ? `Etapa criada em ${nome(config.pipeline_id) || 'funil selecionado'}` : 'Etapa criada em qualquer funil'
    case 'trigger.stage_changed':
      return `${config.field ? `Etapa: mudou ${CAMPO_ETAPA[config.field] || config.field}` : 'Etapa alterada'}${emFunil(config, nome)}`
    case 'trigger.contract_created':
      return 'Contrato criado'
    case 'trigger.contract_status_changed':
    case 'trigger.project_status_changed': {
      const mapa = key.startsWith('trigger.contract') ? CONTRATO : PROJETO
      const alvo = key.startsWith('trigger.contract') ? 'Contrato' : 'Projeto'
      if (config.from_status && config.status) return `${alvo}: ${mapa[config.from_status]} → ${mapa[config.status]}`
      if (config.status) return `${alvo} passou para ${mapa[config.status] || config.status}`
      if (config.from_status) return `${alvo} saiu de ${mapa[config.from_status] || config.from_status}`
      return `Qualquer mudança de status do ${alvo.toLowerCase()}`
    }
    case 'trigger.contract_expiring':
      return config.days ? `Contrato vence em ${config.days} dia(s)` : 'Informe os dias antes do vencimento'
    case 'trigger.project_created':
      return 'Projeto criado'
    case 'trigger.project_deadline_near':
      return config.days ? `Prazo do projeto em ${config.days} dia(s)` : 'Informe os dias antes do prazo'
    case 'trigger.project_overdue':
      return config.days > 1 ? `Projeto atrasado há ${config.days} dias` : 'Projeto com o prazo vencido'
    case 'trigger.client_created': {
      const quem = config.status === 'recorrente' ? 'Cliente recorrente' : config.status === 'cliente' ? 'Cliente' : 'Cliente ou recorrente'
      return config.origem ? `${quem} · origem “${trunc(config.origem, 20)}”` : `${quem} criado`
    }
    case 'action.update_deal_owner':
      return config.user_id ? `Para ${nome(config.user_id) || 'pessoa selecionada'}` : 'Escolha o novo responsável'
    case 'action.add_deal_item': {
      if (!config.name) return 'Informe o item do catálogo'
      const qtd = config.quantidade && Number(config.quantidade) !== 1 ? `${config.quantidade}× ` : ''
      const per = config.kind === 'servico' && config.periodicidade ? ` (${PERIODO[config.periodicidade] || config.periodicidade})` : ''
      return `${qtd}${trunc(config.name, 36)}${per}`
    }
    case 'action.add_deal_contact': {
      if (!config.lookup) return 'Escolha qual lead adicionar'
      const quem = config.lookup === 'flow' ? 'Lead do fluxo' : config.lookup === 'email' ? 'Lead pelo e-mail' : 'Lead pelo telefone'
      const extra = [config.papel && (PAPEL[config.papel] || config.papel), config.is_primary === 'sim' && 'principal'].filter(Boolean)
      return extra.length ? `${quem} · ${extra.join(' · ')}` : quem
    }
    case 'action.archive_deal':
      return 'Arquiva o negócio'
    case 'action.reopen_deal':
      return config.stage_id ? `Reabre em ${nome(config.stage_id) || 'etapa selecionada'}` : 'Reabre na primeira etapa aberta'
    case 'action.duplicate_deal': {
      const partes = []
      if (config.copy_items) partes.push(`itens: ${COPIA[config.copy_items]}`)
      if (config.copy_contacts) partes.push(`leads: ${COPIA[config.copy_contacts]}`)
      return `${config.titulo ? `Cópia “${trunc(config.titulo, 28)}”` : 'Cria uma cópia do negócio'}${partes.length ? ` · ${partes.join(', ')}` : ''}`
    }
    case 'action.set_loss_reason':
      return config.reason ? `Motivo: ${trunc(config.reason, 40)}` : 'Defina o motivo de perda'
    case 'action.set_origin':
      return config.origin ? `Origem: ${trunc(config.origin, 40)}` : 'Defina a origem'
    case 'action.change_pipeline':
      if (!config.pipeline_id) return 'Escolha o funil de destino'
      return `Para ${nome(config.pipeline_id) || 'funil selecionado'}${config.stage_id ? ` / ${nome(config.stage_id) || 'etapa'}` : ''}`
    case 'action.create_contract': {
      const partes = []
      if (config.valor_total) partes.push(`R$ ${config.valor_total}`)
      if (config.mrr) partes.push(`MRR ${config.mrr}`)
      if (config.duration_months) partes.push(`${config.duration_months} mês(es)`)
      return partes.length ? `Contrato: ${partes.join(' · ')}` : 'Cria o contrato do negócio'
    }
    case 'action.update_contract_status':
      return config.status ? `Contrato → ${CONTRATO[config.status] || config.status}` : 'Escolha o novo status'
    case 'action.create_project': {
      if (!config.nome) return 'Defina o nome do projeto'
      return `${trunc(config.nome, 36)}${config.prazo_dias ? ` · prazo ${config.prazo_dias} dia(s)` : ''}`
    }
    case 'action.update_project_status':
      return config.status ? `Projeto → ${PROJETO[config.status] || config.status}` : 'Escolha o novo status'
    case 'action.convert_to_client':
      return config.status === 'recorrente' ? 'Lead vira cliente recorrente' : 'Lead vira cliente'
    default:
      return undefined
  }
}
